import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { ExtraCell, ExtraColumn, ExtraKpi, ExtraSection } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { Button } from '@/components/Button';
import { IconReceipt } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { ErrorState } from '@/components/States';
import { DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { downloadText, sectionToCsv } from '@/features/portal/extraReportCsv';
import { KpiGrid, KpiTile, KpiTileSkeleton } from '@/features/portal/KpiTile';
import { usePortal } from '@/features/portal/PortalContext';
import { fetchExtraReport, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { formatCount, formatMoney, formatPosDate, formatPosDateTime } from '@/lib/format';

type Row = Record<string, ExtraCell>;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

/** Money/percent/count by the column's kind; dates are recognised by shape. */
function renderCell(column: ExtraColumn, value: ExtraCell, currency: string): string {
  if (value === null || value === '') return '—';
  switch (column.kind) {
    case 'money':
      return formatMoney(String(value), currency);
    case 'count':
      return formatCount(Number(value));
    case 'percent':
      return `${value}%`;
    default: {
      const text = String(value);
      if (DATE_ONLY.test(text)) return formatPosDate(text);
      if (DATE_TIME.test(text)) return formatPosDateTime(text);
      return text;
    }
  }
}

function renderKpi(kpi: ExtraKpi, currency: string): string {
  return renderCell({ key: '', label: kpi.label, kind: kpi.kind }, kpi.value, currency);
}

function SectionTable({
  section,
  currency,
  filename,
}: {
  section: ExtraSection;
  currency: string;
  filename: string;
}) {
  const columns = useMemo<Array<Column<Row>>>(
    () =>
      section.columns.map((column, index) => ({
        key: column.key,
        header: column.label,
        align: column.kind === 'text' ? 'left' : 'right',
        pinned: index === 0,
        cellClassName: column.kind === 'text' ? undefined : 'tabular-nums',
        cell: (row) => renderCell(column, row[column.key] ?? null, currency),
      })),
    [section.columns, currency],
  );

  return (
    <SectionCard
      headingId={`extra-${section.title}`}
      title={section.title}
      count={section.rows.length}
      action={
        <Button
          variant="secondary"
          disabled={section.rows.length === 0}
          onClick={() => downloadText(filename, sectionToCsv(section))}
        >
          Download CSV
        </Button>
      }
    >
      {section.rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">Nothing to show for this range.</p>
      ) : (
        <DataTable
          caption={section.title}
          columns={columns}
          rows={section.rows}
          rowKey={(_row, index) => String(index)}
        />
      )}
    </SectionCard>
  );
}

/**
 * Any data-driven report: tiles, then each section as a table with its own CSV
 * download. The report itself is defined on the server (see extra-reports.ts),
 * so a new report needs no page here.
 *
 * [fixedKey] pins the page to one report — Expenses has its own route and menu
 * entry rather than being one of many.
 */
export function ExtraReportPage({ fixedKey }: { fixedKey?: string }) {
  const params = useParams();
  const key = fixedKey ?? params.key ?? '';
  const { currency, filters } = usePortal();
  const range = useMemo(() => toReportRange(filters.filters), [filters.filters]);

  const query = useQuery({
    queryKey: portalKeys.extraReport(key, range),
    queryFn: ({ signal }) => fetchExtraReport(key, range, signal),
    enabled: key !== '',
  });

  const report = query.data;
  const reportCurrency = report?.currency || currency;

  if (query.isError) {
    return (
      <>
        <PageHeader title="Report" description="This report could not be loaded." />
        <div className="card">
          <ErrorState
            error={query.error}
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={report?.title ?? 'Loading…'}
        description={
          report
            ? `${report.description}${report.dated ? '' : ' Not affected by the date range.'}`
            : undefined
        }
      />

      <div className="space-y-6">
        {report && report.kpis.length > 0 ? (
          <KpiGrid>
            {report.kpis.map((kpi) => (
              <KpiTile key={kpi.label} label={kpi.label} value={renderKpi(kpi, reportCurrency)} />
            ))}
          </KpiGrid>
        ) : query.isPending ? (
          <KpiGrid>
            <KpiTileSkeleton />
            <KpiTileSkeleton />
            <KpiTileSkeleton />
          </KpiGrid>
        ) : null}

        <AsyncSection
          state={query}
          skeleton={<TableSkeleton rows={6} columns={5} />}
          emptyIcon={<IconReceipt className="h-5 w-5" />}
        >
          {(data) => (
            <div className="space-y-6">
              {data.sections.map((section) => (
                <SectionTable
                  key={section.title}
                  section={section}
                  currency={reportCurrency}
                  filename={`${data.key}-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${data.range.from}_${data.range.to}.csv`}
                />
              ))}
            </div>
          )}
        </AsyncSection>
      </div>
    </>
  );
}
